// ═══════════════════════════════════════════════════════════════════
// POSTHOG ANALYTICS
// Anonymous behavioral analytics shared by every page through the bundle.
// Do not add form values, contact details, or other user-entered content to
// any event properties in this file.
// ═══════════════════════════════════════════════════════════════════
(function () {
    'use strict';

    const posthog = window.posthog;
    if (!posthog || typeof posthog.capture !== 'function') return;

    const pagePath = window.location.pathname || '/';
    const seenScrollDepths = new Set();
    const seenSections = new Set();
    let contactStarted = false;

    function safeCapture(eventName, properties) {
        try {
            posthog.capture(eventName, Object.assign({ page_path: pagePath }, properties || {}));
        } catch (error) {
            // Analytics must never interfere with site behavior.
        }
    }

    function textLabel(element) {
        if (!element) return '';
        const explicit = element.getAttribute('data-analytics-name') || element.getAttribute('aria-label');
        const text = explicit || element.textContent || '';
        return text.replace(/\s+/g, ' ').trim().slice(0, 100);
    }

    function stableName(element) {
        if (!element) return 'unnamed';
        const explicit = element.getAttribute('data-analytics-name');
        if (explicit) return explicit.slice(0, 100);
        if (element.id) return element.id.slice(0, 100);
        const href = element.getAttribute('href') || '';
        if (href) {
            try {
                const url = new URL(href, window.location.href);
                if (url.hostname === window.location.hostname) {
                    return (url.pathname.replace(/^\/+|\/+$/g, '').replace(/[^a-z0-9]+/gi, '_') || 'home').slice(0, 100);
                }
            } catch (error) {
                // Fall through to the visible label.
            }
        }
        return (textLabel(element) || 'unnamed').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 100) || 'unnamed';
    }

    function pageCategory() {
        if (pagePath === '/' || pagePath === '') return 'homepage';
        if (pagePath.indexOf('/features/') === 0) return 'feature';
        if (pagePath.indexOf('/compensation-plans/') === 0) return 'compensation_plan';
        if (pagePath.indexOf('/resources/') === 0) return 'resource';
        if (pagePath.indexOf('pricing') !== -1) return 'pricing';
        if (pagePath.indexOf('contact') !== -1) return 'contact';
        return 'other';
    }

    function registerPageContext() {
        const params = new URLSearchParams(window.location.search);
        const properties = {
            page_path: pagePath,
            page_title: document.title || '',
            page_category: pageCategory(),
            referrer: ''
        };

        try {
            properties.referrer = document.referrer ? new URL(document.referrer).hostname : '';
        } catch (error) {
            properties.referrer = '';
        }

        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
            const value = params.get(key);
            if (value) properties[key] = value.slice(0, 200);
        });

        try {
            posthog.register(properties);
        } catch (error) {
            // Registration is optional and must not block the page.
        }

        safeCapture('$pageview', properties);
    }

    function closestElement(target, selector) {
        return target && target.closest ? target.closest(selector) : null;
    }

    function sectionName(element) {
        const section = closestElement(element, '[data-analytics-section], section[id], main > header[id], header[id], .feat-section[id], .media-section, .early-cta, .next-steps, .table-wrap, .cta');
        if (!section) return '';
        const explicit = section.getAttribute('data-analytics-section');
        if (explicit) return explicit.slice(0, 100);
        if (section.id) return section.id.slice(0, 100);

        const classNames = ['media-section', 'early-cta', 'next-steps', 'table-wrap', 'cta'];
        const className = classNames.find((name) => section.classList.contains(name));
        if (!className) return '';
        const matching = Array.from(document.querySelectorAll(`.${className}`));
        const index = matching.indexOf(section) + 1;
        return `${className}_${Math.max(index, 1)}`;
    }

    function ctaLocation(element) {
        if (closestElement(element, '#navbar')) return 'navbar';
        if (closestElement(element, '.site-footer')) return 'footer';
        return sectionName(element) || 'page';
    }

    function destinationType(href) {
        if (!href) return 'button';
        if (href.indexOf('mailto:') === 0) return 'email';
        if (href.indexOf('tel:') === 0) return 'phone';
        if (href.indexOf('wa.me') !== -1) return 'whatsapp';
        if (href.charAt(0) === '#') return 'anchor';
        try {
            const url = new URL(href, window.location.href);
            return url.hostname === window.location.hostname ? 'internal' : 'external';
        } catch (error) {
            return 'button';
        }
    }

    function isCta(element) {
        if (!element) return false;
        if (element.matches('.btn, .cta a, .pricing-cta, .site-footer__cta-btn, .contact-submit, .wa-float, .site-footer__link-accent')) return true;
        const label = (textLabel(element) + ' ' + (element.getAttribute('href') || '')).toLowerCase();
        return /demo|pricing|contact|quote|get started|talk|whatsapp|working system|send my enquiry/.test(label);
    }

    function captureContactState(form, eventName, extra) {
        const container = closestElement(form, '#contact-form') || form;
        safeCapture(eventName, Object.assign({
            form_id: form.getAttribute('data-analytics-form-id') || form.id || container.id || 'contact-form',
            form_location: ctaLocation(form)
        }, extra || {}));
    }

    function hasVisibleFormError(form) {
        const container = closestElement(form, '.contact-card, #contact-form') || form;
        return !!container.querySelector('.border-error, .contact-form-alert, .contact-consent-error, .contact-input > small, [role="alert"]');
    }

    function setupContactForms() {
        document.querySelectorAll('form').forEach((form) => {
            const isContactForm = form.id === 'contact-form' || closestElement(form, '#contact-form');
            if (!isContactForm) return;

            form.addEventListener('focusin', () => {
                if (contactStarted) return;
                contactStarted = true;
                captureContactState(form, 'contact_form_started');
            });

            form.addEventListener('submit', () => {
                const valid = typeof form.checkValidity !== 'function' || form.checkValidity();
                if (!valid) {
                    captureContactState(form, 'contact_form_error', { error_type: 'validation' });
                    return;
                }
                captureContactState(form, 'contact_form_submitted');
                window.setTimeout(() => {
                    if (hasVisibleFormError(form)) {
                        captureContactState(form, 'contact_form_error', { error_type: 'server_or_form' });
                    }
                }, 0);
            });
        });

        const success = document.querySelector('#contact-success, .contact-success');
        const serverError = document.querySelector('.contact-form-alert, .contact-consent-error, .contact-input > small');
        if (serverError) {
            const form = document.querySelector('form');
            if (form && (form.id === 'contact-form' || closestElement(form, '#contact-form'))) {
                captureContactState(form, 'contact_form_error', { error_type: 'server_validation' });
            }
        }
        if (!success) return;

        let successCaptured = false;
        const captureSuccess = () => {
            const visible = !success.classList.contains('hidden') && success.offsetParent !== null;
            if (!visible || successCaptured) return;
            successCaptured = true;
            safeCapture('contact_form_success', { form_id: 'contact-form' });
        };

        captureSuccess();
        if ('MutationObserver' in window) {
            const observer = new MutationObserver(captureSuccess);
            observer.observe(success, { attributes: true, attributeFilter: ['class', 'style'] });
        }
    }

    function setupScrollDepth() {
        const thresholds = [25, 50, 75, 90, 100];
        let ticking = false;

        function checkDepth() {
            const documentHeight = Math.max(document.documentElement.scrollHeight, 1);
            const viewportBottom = window.scrollY + window.innerHeight;
            const percentage = Math.min(100, Math.round((viewportBottom / documentHeight) * 100));
            thresholds.forEach((threshold) => {
                if (percentage >= threshold && !seenScrollDepths.has(threshold)) {
                    seenScrollDepths.add(threshold);
                    safeCapture('scroll_depth_reached', { depth_percent: threshold });
                }
            });
            ticking = false;
        }

        window.addEventListener('scroll', () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(checkDepth);
        }, { passive: true });
        checkDepth();
    }

    function setupSectionVisibility() {
        if (!('IntersectionObserver' in window)) return;
        const sections = document.querySelectorAll('main [data-analytics-section], main section[id], main > header[id], main .feat-section[id], main .media-section, main .early-cta, main .next-steps, main .table-wrap, main .cta');
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const sectionId = sectionName(entry.target);
                if (!entry.isIntersecting || !sectionId || seenSections.has(sectionId)) return;
                seenSections.add(sectionId);
                safeCapture('section_viewed', { section_id: sectionId });
                observer.unobserve(entry.target);
            });
        }, { threshold: 0.35 });
        sections.forEach((section) => {
            if (sectionName(section)) observer.observe(section);
        });
    }

    function setupNavigationTracking() {
        const openedMenus = new Set();
        const captureMenuOpen = (menuName) => {
            if (!menuName || openedMenus.has(menuName)) return;
            openedMenus.add(menuName);
            safeCapture('nav_menu_opened', { menu_name: menuName });
        };

        document.querySelectorAll('#navbar [data-dd]').forEach((element) => {
            ['mouseenter', 'focus'].forEach((eventName) => {
                element.addEventListener(eventName, () => {
                    captureMenuOpen(element.getAttribute('data-dd') || stableName(element));
                });
            });
        });

        document.querySelectorAll('#nav-mobile .nav-mobile-parent').forEach((element) => {
            element.addEventListener('click', () => {
                window.requestAnimationFrame(() => {
                    if (element.classList.contains('is-open')) captureMenuOpen(stableName(element));
                });
            });
        });

        const hamburger = document.getElementById('nav-hamburger');
        const mobileMenu = document.getElementById('nav-mobile');
        if (hamburger && mobileMenu) {
            hamburger.addEventListener('click', () => {
                window.requestAnimationFrame(() => {
                    if (mobileMenu.classList.contains('is-open')) captureMenuOpen('mobile_navigation');
                });
            });
        }
    }

    function setupFaqTracking() {
        document.querySelectorAll('details.faq, details.feat-faq').forEach((faq) => {
            faq.addEventListener('toggle', () => {
                if (!faq.open) return;
                safeCapture('faq_opened', {
                    faq_name: textLabel(faq.querySelector('summary')) || stableName(faq)
                });
            });
        });
    }

    function setupClickTracking() {
        document.addEventListener('click', (event) => {
            const element = closestElement(event.target, 'a, button');
            if (!element) return;

            const href = element.getAttribute('href') || '';
            const label = textLabel(element);
            const inNavbar = !!closestElement(element, '#navbar');
            const type = destinationType(href);

            if (inNavbar && element.matches('a')) {
                safeCapture('nav_link_clicked', {
                    link_label: label,
                    destination_type: type,
                    destination: href.split('?')[0].slice(0, 200)
                });
            }

            if (type === 'whatsapp') safeCapture('whatsapp_clicked', { cta_location: ctaLocation(element) });
            if (type === 'phone') safeCapture('phone_clicked', { cta_location: ctaLocation(element) });
            if (type === 'email') safeCapture('email_clicked', { cta_location: ctaLocation(element) });

            if (type === 'external' && !/wa\.me/.test(href)) {
                safeCapture('outbound_link_clicked', {
                    link_label: label,
                    destination: href.split('?')[0].slice(0, 200),
                    cta_location: ctaLocation(element)
                });
            }

            if (isCta(element)) {
                safeCapture('cta_clicked', {
                    cta_name: stableName(element),
                    cta_location: ctaLocation(element),
                    destination_type: type
                });
            }

            const pricingSection = closestElement(element, '#pricing, .pricing-page');
            if (pricingSection) {
                safeCapture('pricing_interaction', {
                    interaction_type: 'click',
                    target_name: stableName(element)
                });
            }

            const planPage = pagePath.match(/\/compensation-plans\/([^/]+)/);
            if (planPage) {
                safeCapture('compensation_plan_interaction', {
                    plan_slug: planPage[1],
                    interaction_type: 'click',
                    target_name: stableName(element)
                });
            }

            const faq = closestElement(element, '.faq-item');
            if (faq && (element.matches('.faq-toggle') || element.closest('.faq-toggle'))) {
                const isOpen = faq.classList.contains('is-open');
                if (isOpen) {
                    safeCapture('faq_opened', { faq_name: textLabel(faq.querySelector('.faq-toggle, summary')) });
                }
            }
        });
    }

    registerPageContext();
    setupContactForms();
    setupScrollDepth();
    setupSectionVisibility();
    setupNavigationTracking();
    setupFaqTracking();
    setupClickTracking();
})();
